/**
 * ErrorState — Generic error screen used by all three modules.
 * Detects rate-limit / auth errors and shows a tailored amber warning panel.
 */

function isRateLimit(msg: string) {
  return /rate.?limit|insufficient.quota|quota.exceed|\u26a0.*rate/i.test(msg)
}
function isAuthError(msg: string) {
  return /invalid.api.key|AuthenticationError|\u26a0.*invalid.api/i.test(msg)
}

interface ErrorStateProps {
  message:  string
  onRetry:  () => void
  onReset:  () => void
  retryLabel?: string
  resetLabel?: string
}

export default function ErrorState({
  message, onRetry, onReset, retryLabel = 'Try again', resetLabel = 'Start over',
}: ErrorStateProps) {
  const rateLimit = isRateLimit(message)
  const authError = isAuthError(message)
  const isApiKey  = rateLimit || authError

  return (
    <div className="flex flex-col items-center justify-start pt-20 pb-24 px-5 animate-fade-up">
      <div className="w-full max-w-md">
        <div className={`bg-white rounded-2xl shadow-card p-8 text-center border ${
          isApiKey ? 'border-amber-200' : 'border-red-100'
        }`}>
          <div className={`mx-auto mb-5 w-12 h-12 rounded-full flex items-center justify-center ${
            isApiKey ? 'bg-amber-50' : 'bg-red-50'
          }`}>
            {isApiKey ? (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                <path d="M11 2L2 20h18L11 2z" stroke="#D97706" strokeWidth="1.6"
                      strokeLinejoin="round"/>
                <path d="M11 8v5M11 16h.01" stroke="#D97706" strokeWidth="1.6"
                      strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 6v5M10 14h.01" stroke="#EF4444" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="10" cy="10" r="8.5" stroke="#EF4444" strokeWidth="1.5"/>
              </svg>
            )}
          </div>

          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            {rateLimit ? 'API quota exceeded' : authError ? 'API key error' : 'Something went wrong'}
          </h2>

          {/* Rate-limit specific guidance */}
          {rateLimit && (
            <div className="mb-4 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-left">
              <p className="text-sm text-amber-800 leading-relaxed">
                Your OpenAI key has hit its usage limit. The job was stopped automatically.
              </p>
              <a
                href="https://platform.openai.com/usage"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700
                           underline hover:text-amber-900"
              >
                Check usage dashboard
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path d="M4 2h4v4M8 2 3 7" stroke="currentColor" strokeWidth="1.3"
                        strokeLinecap="round"/>
                </svg>
              </a>
            </div>
          )}

          {/* Auth error guidance */}
          {authError && (
            <div className="mb-4 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-left">
              <p className="text-sm text-amber-800 leading-relaxed">
                The OpenAI API key is invalid or revoked.
                Check <code className="bg-amber-100 px-1 rounded">OPENAI_API_KEY</code> in your .env file.
              </p>
            </div>
          )}

          {message && (
            <p className="text-sm text-slate-500 mb-6 leading-relaxed bg-slate-50
                          rounded-xl px-4 py-3 text-left font-mono break-words">
              {message}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onRetry}
              className="flex-1 sm:flex-none px-6 py-2.5 bg-navy-900 text-white rounded-xl
                         text-sm font-medium hover:bg-navy-800 transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              {retryLabel}
            </button>
            <button
              onClick={onReset}
              className="flex-1 sm:flex-none px-6 py-2.5 bg-white text-slate-600
                         border border-slate-200 rounded-xl text-sm font-medium
                         hover:border-slate-400 hover:text-slate-900 transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              {resetLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
